import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { Button as MuiButton } from '@mui/material';

import { BATCH_SUPPORTED_OPERATIONS, BatchValidationError } from '../interfaces';
import { batchCompleteRouteCardOperations } from '../api/batchCompleteRouteCardOperations';

interface BatchCompleteOperationsPopoverProps {
    selectedDeviceIds: string[];
    onComplete: () => void;
}

export function BatchCompleteOperationsPopover({
    selectedDeviceIds,
    onComplete,
}: BatchCompleteOperationsPopoverProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedOperationIds, setSelectedOperationIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<BatchValidationError[] | null>(null);

    const isOpen = Boolean(anchorEl);
    const isDisabled = selectedDeviceIds.length < 2;

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setSelectedOperationIds(new Set());
        setValidationErrors(null);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedOperationIds(new Set());
        setValidationErrors(null);
    };

    const handleToggleOperation = (operationId: string) => {
        setSelectedOperationIds((prev) => {
            const next = new Set(prev);
            if (next.has(operationId)) {
                next.delete(operationId);
            } else {
                next.add(operationId);
            }
            return next;
        });
        setValidationErrors(null);
    };

    const handleComplete = async () => {
        setLoading(true);
        setValidationErrors(null);
        try {
            await batchCompleteRouteCardOperations({
                deviceIds: selectedDeviceIds,
                operationIds: Array.from(selectedOperationIds),
            });
            handleClose();
            onComplete();
        } catch (err: any) {
            const details = err?.response?.data?.details;
            if (details?.errors && Array.isArray(details.errors)) {
                setValidationErrors(details.errors);
            } else {
                setValidationErrors([
                    {
                        deviceId: '',
                        reasons: [err?.response?.data?.message ?? 'An unexpected error occurred'],
                    },
                ]);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <MuiButton
                startIcon={<PlaylistAddCheckIcon />}
                disabled={isDisabled}
                color="primary"
                onClick={handleOpen}
                data-testid="batch-operations-button"
            >
                Batch Operations
            </MuiButton>

            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ p: 2, minWidth: 280, maxWidth: 360 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Complete operations for {selectedDeviceIds.length} devices
                    </Typography>

                    <FormGroup>
                        {BATCH_SUPPORTED_OPERATIONS.map(({ operationId, name }) => (
                            <FormControlLabel
                                key={operationId}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedOperationIds.has(operationId)}
                                        onChange={() => handleToggleOperation(operationId)}
                                        disabled={loading}
                                        data-testid={`operation-checkbox-${operationId}`}
                                    />
                                }
                                label={name}
                            />
                        ))}
                    </FormGroup>

                    {validationErrors && (
                        <Box sx={{ mt: 1 }}>
                            <Alert severity="error" sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Validation failed for the following devices:
                                </Typography>
                                <List dense disablePadding>
                                    {validationErrors.map((err, i) => (
                                        <ListItem
                                            key={i}
                                            disableGutters
                                            disablePadding
                                            sx={{ alignItems: 'flex-start' }}
                                        >
                                            <ListItemText
                                                primary={err.deviceId || 'Error'}
                                                secondary={err.reasons.join('; ')}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                                secondaryTypographyProps={{ variant: 'caption' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Alert>
                        </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button size="small" onClick={handleClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={selectedOperationIds.size === 0 || loading}
                            onClick={handleComplete}
                            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
                            data-testid="batch-complete-button"
                        >
                            Complete
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
